import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Terminal, Lock, Eye, EyeOff, Shield, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

/**
 * /set-password
 *
 * Landing page for the admin one-time magic-link flow.
 * Supabase magic links embed #access_token & #refresh_token in the URL hash.
 * We pick those up, set the session, then require a strong new password.
 * The user_metadata.force_password_change flag is cleared on success.
 */
const SetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  // ── Consume hash tokens from Supabase magic link ──────────────────────────
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) {
      setSessionError("No authentication token found in URL. This link may have expired or already been used.");
      return;
    }

    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        if (session) {
          setSessionReady(true);
          return;
        }
        // Try to exchange hash tokens
        const { error } = await supabase.auth.exchangeCodeForSession(
          window.location.href
        );
        if (error) {
          setSessionError(error.message);
        } else {
          setSessionReady(true);
        }
      });
  }, []);

  const validate = (): string | null => {
    if (password.length < 12)
      return "Password must be at least 12 characters.";
    if (!/[A-Z]/.test(password))
      return "Password must contain at least one uppercase letter.";
    if (!/[0-9]/.test(password))
      return "Password must contain at least one number.";
    if (!/[^A-Za-z0-9]/.test(password))
      return "Password must contain at least one special character.";
    if (password !== confirm)
      return "Passwords do not match.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      toast({ title: "Invalid password", description: validationError, variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password,
        data: { force_password_change: false },
      });
      if (error) throw error;

      toast({ title: "Password set!", description: "You are now logged in. Welcome, Admin." });
      navigate("/admin");
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-dark">
      <div className="fixed inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
      <div className="fixed inset-0 bg-gradient-radial from-primary/5 via-transparent to-transparent pointer-events-none" />

      <div className="flex-1 flex items-center justify-center px-4 relative z-10">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-neon">
                <Terminal className="w-6 h-6 text-primary-foreground" />
              </div>
              <div className="text-left">
                <h1 className="text-xl font-bold text-gradient-cyber inline-block">JP AI Dorks</h1>
                <p className="text-xs text-muted-foreground">Enterprise OSINT Platform</p>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-foreground">Set Your Password</h2>
            <p className="text-muted-foreground text-sm mt-1">
              One-time admin setup — choose a strong password.
            </p>
          </div>

          {/* Session error state */}
          {sessionError && (
            <div className="glass-card p-5 mb-4 border border-destructive/40 text-destructive text-sm">
              <p className="font-semibold mb-1">❌ Link Invalid or Expired</p>
              <p className="text-xs text-muted-foreground">{sessionError}</p>
              <p className="text-xs text-muted-foreground mt-2">
                Run the bootstrap endpoint again to generate a new one-time link.
              </p>
            </div>
          )}

          {/* Loading state */}
          {!sessionReady && !sessionError && (
            <div className="glass-card p-8 text-center text-muted-foreground text-sm">
              Verifying authentication token…
            </div>
          )}

          {/* Password form */}
          {sessionReady && (
            <form onSubmit={handleSubmit} className="glass-card p-6 space-y-4">
              <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg text-xs text-primary">
                <CheckCircle2 className="w-4 h-4 inline mr-1" />
                Identity verified. Set a strong password to complete setup.
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 12 chars, uppercase, number, symbol"
                    className="w-full pl-10 pr-12 py-3 bg-muted/30 border border-border/50 rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat password"
                    className="w-full pl-10 pr-4 py-3 bg-muted/30 border border-border/50 rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              </div>

              {/* Strength hints */}
              <ul className="text-xs text-muted-foreground space-y-0.5 pl-1">
                {[
                  ["12+ characters", password.length >= 12],
                  ["Uppercase letter", /[A-Z]/.test(password)],
                  ["Number", /[0-9]/.test(password)],
                  ["Special character", /[^A-Za-z0-9]/.test(password)],
                  ["Passwords match", password !== "" && password === confirm],
                ].map(([label, ok]) => (
                  <li key={label as string} className={`flex items-center gap-1.5 ${ok ? "text-green-400" : ""}`}>
                    <span>{ok ? "✅" : "○"}</span> {label as string}
                  </li>
                ))}
              </ul>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-lg bg-gradient-to-r from-primary to-secondary text-primary-foreground font-semibold transition-all hover:shadow-neon disabled:opacity-50"
              >
                {submitting ? "Setting password…" : "Set Password & Enter Admin"}
              </button>
            </form>
          )}

          <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground/70">
            <Shield className="w-4 h-4 mt-0.5 shrink-0" />
            <p>This link is single-use and expires in 1 hour. For security, do not share it.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
